import React from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { TypeStep, TypeStepData } from '@REDUCERS';
import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';
import { useGetAllProperties } from '@SERVICES';

import Grid from '@COMPONENTS/SHARED/Grid';
import SquareButton from '@COMPONENTS/COMMON/buttons/SquareButton';
import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import EmptyStateHint from '@COMPONENTS/SHARED/EmptyStateHint';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import styles from './Property.module.scss';

const cx: CX = classnames.bind(styles);

function PropertyPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps(() => true);

    const {
        data: propertiesData = [], isLoading, isError, error,
    } = useGetAllProperties();

    const navigate = useNavigate();

    const typeStep: TypeStep = documentGenerationStepsServices.getStepData(GenerationSteps.TYPE);
    const hasSelectedType = typeStep.isComplete;

    function handleClick(id: number, name: string) {
        const data: TypeStepData = {
            id,
            name,
        };

        documentGenerationStepsActions.apply({
            id: GenerationSteps.TYPE,
            isComplete: true,
            data,
        });

        navigate(ROUTES.brand.path);
    }

    function getHighlighted(id: number) {
        const step: TypeStep = documentGenerationStepsServices.getStepData(GenerationSteps.TYPE);

        return step.data?.id === id;
    }

    const handleNext = () => {
        if (hasSelectedType) {
            navigate(ROUTES.brand.path);
        }
    };

    return (
        <div className={cx('page')}>
            <h1>Type</h1>
            <DataLoader
                isLoading={isLoading}
                isError={isError}
                error={error}
            >
                {propertiesData.length === 0 ? (
                    <EmptyStateHint
                        type="types"
                        title="No Property Types Found"
                        description="It looks like there are no property types configured yet. Property types help categorize your documents and determine which content is relevant. You can add new property types in the app settings."
                    />
                ) : (
                    <Grid>
                        {propertiesData.map((property) => {
                            const key = property.id;
                            const highlighted = getHighlighted(property.id);

                            return (
                                <SquareButton
                                    key={key}
                                    highlighted={highlighted}
                                    title={property.name}
                                    onClick={() => handleClick(property.id, property.name)}
                                />
                            );
                        })}
                    </Grid>
                )}
            </DataLoader>

            <StepNavigation
                onNext={handleNext}
                nextDisabled={!hasSelectedType}
                showNext={hasSelectedType}
                nextText="Continue to Brand"
                sticky
            />
        </div>
    );
}

export default PropertyPage;
